import { atom, selector } from 'recoil';
import {
	TAdminTeamRow,
	TNotificationConfigs,
	TAdminCompanyDetails,
	TJWT,
	AuthenticatedUser,
	Role,
	TPermissionConfigs, TRole
} from '@types';
import { ActiveCarrier, ActiveShipper, DomainContext } from './App';

/* eslint-disable */
const keys = {
	JWT:					'@Ofload/JWT',
	DECODED_JWT:			'@Ofload/DecodedJWT',
	USERID:					'@Ofload/UserID',
	EOS_ID:					'@Ofload/EOS_ID',
	USERTYPE:				'@Ofload/User/UserType',
	COMPANYID:				'@Ofload/CompanyID',
	ISADMIN:				'@Ofload/IsAdmin',
	USERNAME:				'@Ofload/UserName',
	COMPANYNAME:			'@Ofload/CompanyName',
  	USER_ROLES: 			'@Ofload/UserRoles',
	WAIT_FOR_GUEST:			'@Ofload/WaitForGuest',
	AUTHENTICATED_USER:		'@Ofload/User/CurrentUser',
	MOST_PRIV_ROLE:			'@Ofload/User/MostPrivilegedRole',
	TEAM_MEMBER_DETAILS:	'@Ofload/TeamMemberDetails',
	TEAM_MEMBER_COMS:		'@Ofload/TeamMemberComs',
	TEAM_MEMBER_PERMS:		'@Ofload/TeamMemberPerms',
	COMPANY_DETAILS:		'@Ofload/CompanyDetails',
	COMPANY_ROLES:			'@Ofload/CompanyRoles',
	X_CUSTOMER_ID:			'@Ofload/XCustomerID'
}
/* eslint-enable */

export const authenticatedUser = atom<AuthenticatedUser | null>({
	key: keys.AUTHENTICATED_USER,
	default: null
});

export const JWT = atom<string | null>({
	key: keys.JWT,
	default: null
});

export const DecodedJWT = atom<TJWT | null>({
	key: keys.DECODED_JWT,
	default: null
});

export const UserID = atom<number | string | null>({
	key: keys.USERID,
	default: null
});

export const EOSID = atom<number | null>({
	key: keys.EOS_ID,
	default: null
});

export const JWTUserType = atom<number | string | null>({
	key: keys.USERTYPE,
	default: null
});

export const CompanyID = atom<number | string | null>({
	key: keys.COMPANYID,
	default: null
});

export const IsAdmin = atom<boolean>({
	key: keys.ISADMIN,
	default: false
});

export const XCustomerID = selector<string | null>({
	key: keys.X_CUSTOMER_ID,
	get: ({ get }) => {
		const userType = get(JWTUserType);
		const customerID = get(DecodedJWT)?.customer_id;
		const isAdmin = get(IsAdmin);
		const context = get(DomainContext);
		const activeShipperCustomerID = get(ActiveShipper)?.customer_id;
		const activeCarrierCustomerID = get(ActiveCarrier)?.customer_id;
		if (!isAdmin && userType !== 'shipper' && userType !== 'carrier') return null;
		// Domain-scoped: a /carrier page must never resolve a shipper id, and vice-versa.
		if (context === 'carrier') {
			if (activeCarrierCustomerID) return `${activeCarrierCustomerID}`;
			return (!isAdmin && customerID) ? `${customerID}` : null;
		}
		if (context === 'shipper') {
			if (activeShipperCustomerID) return `${activeShipperCustomerID}`;
			return (!isAdmin && customerID) ? `${customerID}` : null;
		}
		// No domain context (admin/config + linked rate-card steps): legacy priority.
		if (activeShipperCustomerID) return `${activeShipperCustomerID}`;
		if (activeCarrierCustomerID) return `${activeCarrierCustomerID}`;
		if (!isAdmin && customerID) return `${customerID}`;
		return null;
	}
});

/**
 * Returns the customer ID scoped to the current domain context:
 * - `/shipper/*` → active impersonated shipper's customer_id, or JWT customer_id for non-admin shippers
 * - `/carrier/*` → active impersonated carrier's customer_id, or JWT customer_id for non-admin carriers
 * - All other pages (admin, etc.) → null
 *
 * ActiveShipper and ActiveCarrier atoms are left untouched, so navigating away and back
 * still remembers the previously chosen shipper/carrier.
 */
export const DomainCustomerID = selector<string | null>({
	key: '@Ofload/DomainCustomerID',
	get: ({ get }) => {
		const context = get(DomainContext);
		if (!context) return null;

		const isAdmin = get(IsAdmin);
		const jwtCustomerID = get(DecodedJWT)?.customer_id ?? null;

		if (context === 'shipper') {
			const impersonatedID = get(ActiveShipper)?.customer_id;
			return impersonatedID ? `${impersonatedID}` : (!isAdmin ? jwtCustomerID : null);
		}
		if (context === 'carrier') {
			const impersonatedID = get(ActiveCarrier)?.customer_id;
			return impersonatedID ? `${impersonatedID}` : (!isAdmin ? jwtCustomerID : null);
		}
		return null;
	}
});

export const UserName = selector<string>({
	key: keys.USERNAME,
	get: ({ get }) => {
		const user = get(authenticatedUser);
		return user ? `${user.team.first_name} ${user.team.last_name}` : '';
	}
});

export const CompanyName = selector<string>({
	key: keys.COMPANYNAME,
	get: ({ get }) => {
		const user = get(authenticatedUser);
		return user ? user.company.company_name : '';
	}
});

export const UserRoles = atom<string[]>({
	key: keys.USER_ROLES,
	default: []
});

export const WaitForGuest = atom<boolean>({
	key: keys.WAIT_FOR_GUEST,
	default: true
});

export const MostPrivRole = selector<Role | null>({
	key: keys.MOST_PRIV_ROLE,
	get: ({ get }) => {
		const user = get(authenticatedUser);
		const roles = user?.roles;
		if (roles) {
			const sorted = [...roles].sort((a, b) => {
				return a.access_level > b.access_level ?
					1 : a.access_level < b.access_level ?
						-1 : 0;
			});
			return sorted[0];
		}
		return null;
	}
});

export const NewMember = {
	id: 0,
	first_name: '',
	firstname: '',
	last_name: '',
	lastname: '',
	position: '',
	title: '',
	display_name: '',
	email: '',
	phone: '',
	timezone: '',
	trade_name: '',
	can_access_portal: true,
	user_role: [],
	user_id: 0,
	access_level: 0,
	is_primary: false,
	external_id: '',
	can_see_all_cost_centers: false,
	is_mobile_user: false,
	notification_configurations: {},
	company_id: '',
	abn: '',
	company_name: '',
	team_id: '',
	primary_team_id: null,
	primary_trading_name: '',
	permissions: {
		can_edit_admin_rate_cards: false,
		can_edit_shipper_rate_cards: false,
		can_edit_carrier_rate_cards: false,
		can_edit_regions: false,
		can_edit_shipper_tags: false,
		can_access_autoallocation_setup: false,
		can_edit_linehaul: false,
		can_edit_demurrage_rate_cards: false,
		can_auto_approve_bulk_issue_and_claims: false,
		can_bulk_upload_issue_and_claims: false,
		can_view_finance_audit_trail: false,
	},
}

export const TeamMemberDetails = atom<TAdminTeamRow | null>({
	key: keys.TEAM_MEMBER_DETAILS,
	default: null
});

export const CompanyRoles = atom<TRole[]>({
	key: keys.COMPANY_ROLES,
	default: []
});

export const TeamMemberComs = atom<TNotificationConfigs | null>({
	key: keys.TEAM_MEMBER_COMS,
	default: null
});

export const CompanyDetails = atom<TAdminCompanyDetails | null>({
	key: keys.COMPANY_DETAILS,
	default: null
});

export const TeamMemberPerms = atom<TPermissionConfigs | null>({
	key: keys.TEAM_MEMBER_PERMS,
	default: null
});